import DownloadIcon from '@mui/icons-material/DownloadOutlined';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from 'components/Button/Button';
import DataTable from 'components/DataTable';
import Modal from 'components/Modal/Modal';
import Spin from 'components/Spin';
import FileSaver from 'file-saver';
import useRequest from 'hooks/useRequest';
import orderBy from 'lodash/orderBy';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Bar,
  BarChart,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  getConnectCardReport,
  getConnectCardSubmissionReport,
} from 'services/connect-card.service';
import { toUserTimezone } from 'utils';
import { FIELD_LABELS } from '../../constants/field-types';
import { getCardCompletionRate } from '../../utils';

const ConnectCardReport = ({ onClose, data }) => {
  const [report, setReport] = useState(null);
  const [submissionReport, { loading }] = useRequest(
    getConnectCardSubmissionReport,
    data.hash,
  );
  const account = useSelector((state) => state.settings.account);
  const [sort, setSort] = useState({
    sortField: 'completed_at',
    sortOrder: 'desc',
  });

  useEffect(() => {
    getConnectCardReport(data.hash).then((resp) => {
      setReport(resp);
    });
  }, []);

  const exportData = async () => {
    const res = await getConnectCardSubmissionReport(data.hash, true);
    const blob = new Blob([res], { type: 'text/csv;charset=utf-8' });
    FileSaver.saveAs(blob, 'connect_card_export.csv');
  };

  const onTableChange = (type, newState) => {
    if (type === 'sort') {
      setSort(newState);
    }
  };

  const visitData = useMemo(() => {
    if (report) {
      return report.total_answer_by_date.map((i) => ({
        name: moment()
          .startOf('month')
          .set('month', +i.month - 1)
          .format('MMM'),
        value: i.total_submission,
      }));
    }
    return [];
  }, [report]);

  const pieData = [
    { name: 'Visits', value: +report?.card_visits },
    { name: 'Completed', value: +report?.card_completions },
  ];

  const completionData = useMemo(() => {
    if (!report) return [];
    const completions = +report?.card_completions;
    return report.question_report.map((i) => ({
      name: FIELD_LABELS[i.question_type],
      value: +((+i.total_answer * 100) / completions).toFixed(0),
    }));
  }, [report]);

  const columns = useMemo(() => {
    if (!submissionReport || submissionReport.length === 0) return [];
    const row = submissionReport[0];
    const keys = Object.keys(row).filter((k) => k.startsWith('Q'));

    return [
      {
        text: 'Completed Date',
        dataField: 'Date Completed',
        sortable: true,
        formatter: (val) =>
          toUserTimezone(val, account.account_timezone).format('L'),
      },
      ...keys.map((k) => ({
        text: k,
        dataField: k,
        sortable: true,
      })),
    ];
  }, [submissionReport]);

  const tableData = useMemo(() => {
    const { sortField, sortOrder } = sort;
    return orderBy(
      submissionReport,
      [(i) => (i[sortField] || '').toLowerCase()],
      [sortOrder],
    );
  }, [submissionReport, sort]);

  const axisStyle = {
    fontSize: '12px',
    color: '#A0A2B2',
  };

  return (
    <Modal
      open
      fullWidth
      maxWidth="lg"
      title="Connect Card Usage Report"
      onClose={onClose}
      actions={[
        <Button startIcon={<DownloadIcon />} onClick={exportData}>
          Download (.CSV)
        </Button>,
      ]}
    >
      <Typography paragraph color="text.secondary">
        {data.card_name}
      </Typography>
      {report ? (
        <>
          <Stack spacing={2} direction="row" my={2}>
            <Paper
              variant="outlinedVariant"
              sx={{ p: 2, textAlign: 'center', color: '#8235F4', flex: 1 }}
            >
              <Typography color="inherit" variant="h1" fontWeight={600}>
                {report.card_visits}
              </Typography>
              <Typography color="inherit" variant="caption">
                Visits
              </Typography>
            </Paper>
            <Paper
              variant="outlinedVariant"
              sx={{ p: 2, textAlign: 'center', color: '#FFB800', flex: 1 }}
            >
              <Typography color="inherit" variant="h1" fontWeight={600}>
                {report.card_completions}
              </Typography>
              <Typography color="inherit" variant="caption">
                Completed
              </Typography>
            </Paper>
            <Paper
              variant="outlinedVariant"
              sx={{ p: 2, textAlign: 'center', color: '#038AFF', flex: 1 }}
            >
              <Typography color="inherit" variant="h1" fontWeight={600}>
                {getCardCompletionRate(report)}%
              </Typography>
              <Typography color="inherit" variant="caption">
                Completion rate
              </Typography>
            </Paper>
          </Stack>
          <Stack spacing={2} mb={2} direction="row">
            <Paper sx={{ p: 2, flex: 1 }} variant="outlinedVariant">
              <Typography>Visits</Typography>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart
                  data={visitData}
                  margin={{
                    top: 5,
                    right: 0,
                    left: 0,
                    bottom: 0,
                  }}
                >
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#8235F4"
                    strokeWidth={3}
                  />
                  <XAxis tickLine={false} style={axisStyle} dataKey="name" />
                  <YAxis style={axisStyle} />
                  <Tooltip />
                </LineChart>
              </ResponsiveContainer>
            </Paper>
            <Paper sx={{ p: 2, flex: 1 }} variant="outlinedVariant">
              <Typography>Completed</Typography>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    dataKey="value"
                  >
                    <Cell fill="#8235F4" />
                    <Cell fill="#FFB800" />
                  </Pie>
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </Paper>
            <Paper sx={{ p: 2, flex: 1 }} variant="outlinedVariant">
              <Typography>Completion Rate</Typography>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart
                  layout="vertical"
                  data={completionData}
                  margin={{
                    top: 5,
                    right: 0,
                    left: 0,
                    bottom: 0,
                  }}
                >
                  <Bar dataKey="value" barSize={10} fill="#038AFF" />
                  <XAxis
                    tickLine={false}
                    unit="%"
                    type="number"
                    style={axisStyle}
                  />
                  <YAxis
                    style={axisStyle}
                    width={100}
                    dataKey="name"
                    type="category"
                    scale="auto"
                  />
                  <Tooltip cursor={false} />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Stack>
          <DataTable
            pagination
            bordered
            loading={loading}
            columns={columns}
            data={tableData}
            defaultSorted={sort}
            onTableChange={onTableChange}
          />
        </>
      ) : (
        <Spin spinning />
      )}
    </Modal>
  );
};

export default ConnectCardReport;
